import type { MetricRecord } from "@/shared/domain/metrics";
import {
  cellStyles,
  type ColDef,
  type ColumnPickerOption,
} from "@/shared/foundation/primitives/Table";
import { Principal } from "@/shared/principal";

import {
  MetricNameCell,
  PublishedCell,
  RecordsCell,
  ValueCell,
} from "./MetricCells";
import type { MetricTreeRow } from "./metricLeaves";

/** A metric's newest record's `field`, for the optional columns; empty on a group. */
function latestField(
  field: keyof Pick<MetricRecord, "unit" | "invocation_id" | "device_id">,
): (params: { data?: MetricTreeRow }) => string | null | undefined {
  return ({ data }) =>
    data?.kind === "leaf" ? data.row.latest?.[field] : undefined;
}

export const METRIC_COLUMNS: ColDef<MetricTreeRow>[] = [
  {
    colId: "name",
    headerName: "Metric",
    flex: 1,
    minWidth: 240,
    hide: false,
    lockVisible: true,
    sortable: true,
    cellRenderer: MetricNameCell,
  },
  {
    colId: "records",
    headerName: "Records",
    width: 110,
    type: "rightAligned",
    sortable: true,
    cellRenderer: RecordsCell,
  },
  {
    colId: "value",
    headerName: "Latest value",
    width: 150,
    type: "rightAligned",
    sortable: true,
    cellRenderer: ValueCell,
  },
  {
    colId: "published",
    headerName: "Last published",
    width: 170,
    sortable: true,
    cellRenderer: PublishedCell,
  },
  {
    colId: "unit",
    headerName: "Unit",
    width: 110,
    valueGetter: latestField("unit"),
  },
  {
    colId: "published_by",
    headerName: "Published by",
    width: 180,
    valueGetter: ({ data }) => {
      const publishedBy =
        data?.kind === "leaf" ? data.row.latest?.published_by : undefined;
      return publishedBy === undefined
        ? undefined
        : (Principal.fromString(publishedBy).ok?.unqualifiedId ?? publishedBy);
    },
  },
  {
    colId: "invocation_id",
    headerName: "Invocation",
    width: 180,
    cellClass: cellStyles.idCell,
    valueGetter: latestField("invocation_id"),
  },
  {
    colId: "device_id",
    headerName: "Device",
    width: 180,
    cellClass: cellStyles.idCell,
    valueGetter: latestField("device_id"),
  },
];

/** The columns shown until the user picks others; Metric is always shown. */
export const DEFAULT_VISIBLE_COLUMN_IDS = ["records", "value", "published"];

/** The columns the picker offers; Metric is always shown. */
export const COLUMN_PICKER_OPTIONS: ColumnPickerOption[] =
  METRIC_COLUMNS.filter((column) => !column.lockVisible).map((column) => ({
    id: column.colId!,
    label: column.headerName!,
  }));
